import { supabase } from '../supabase';
import { currentDivisionId } from '../stores/useFiltersStore';
import {
  Shipment,
  ShipmentBox,
  ShipmentShipTrip,
  ShipmentTrackingEvent,
  ShipmentTrackingEventRevision,
  ShipmentTrackingMilestoneSummary,
  ShipmentTrackingSummary,
} from '../utils/types';
import {
  findShipmentUploadIdentifierConflicts,
  isValidShipmentReference,
  normalizeShipmentReference,
  type ShipmentUploadIdentifierConflict,
  type ShipmentUploadIdentifierRow,
} from '../utils/shipment-upload-validation';

import {
  decodeShipmentOverview,
  type ShipmentOverviewPayload,
} from '../utils/shipment-overview';

export interface ShipmentSettlementCounts {
  total_shipments: number;
  settled_shipments: number;
}

export type ShipmentDetailsPageRow = Pick<
  ShipmentBox,
  | 'id'
  | 'shipment_id'
  | 'exporter_id'
  | 'season'
  | 'species_id'
  | 'reference_number'
  | 'exporter_receiver_id'
  | 'receiver'
  | 'packing_name'
  | 'ship_name'
  | 'ship_etd'
  | 'ship_eta'
  | 'quantity'
  | 'box_weight'
  | 'variety'
  | 'caliber'
  | 'color'
  | 'category'
  | 'box_format'
  | 'box_label'
  | 'ship_destination_country'
  | 'ship_destination_port'
  | 'ship_type'
  | 'business_type'
  | 'sale_order'
  | 'original_incoterm'
  | 'sale_region'
  | 'container_number'
  | 'awb_number'
  | 'box_type'
> & {
  labeled_variety?: string | null;
  tracking_journeys?: ShipmentBox['tracking_journeys'];
  status?: 'settled' | 'pending';
  estimated_settlement_date?: Date | '';
};

export interface ShipmentsDetailsPagePayload {
  rows: ShipmentDetailsPageRow[];
  tracking_by_shipment?: Array<{
    shipment_id: number;
    tracking_journeys: ShipmentTrackingSummary[];
  }>;
  status_counts: ShipmentSettlementCounts;
}

interface ShipmentTrackingEventSummaryRow {
  tracking_journey_id: string;
  provider_container_status?: string | null;
  observation_status?: ShipmentTrackingSummary['observation_status'];
  observation_closes_on?: string | null;
  cargo_arrived_at?: string | null;
  gate_out_at?: string | null;
  empty_return_at?: string | null;
  last_sync_succeeded_at?: string | null;
  consecutive_sync_failures?: number;
  latest_actual_event?: ShipmentTrackingMilestoneSummary | null;
  latest_estimated_event?: ShipmentTrackingMilestoneSummary | null;
}

export interface ShipmentTrackingReferenceSummary {
  reference_key: string;
  tracking_journeys: ShipmentTrackingSummary[];
}

export interface ShipmentTrackingRefreshResult {
  success: boolean;
  sync_run_id?: string;
  synced: number;
  succeeded: number;
  failed: number;
  error?: string;
  results?: Array<{
    tracking_journey_id: string;
    result: string;
    error?: string;
    error_code?: string;
  }>;
}

export interface ShipmentTrackingDiscoveryResult {
  success: boolean;
  worker_run_id?: string;
  error?: string;
  counts: {
    scanned_shipments: number;
    resolved_identifiers: number;
    invalid_identifier_values: number;
    missing_identifier: number;
    invalid_identifier: number;
    selection_required: number;
    missing_dates: number;
    invalid_dates: number;
    scheduled: number;
    eligible: number;
    window_expired: number;
    journeys_created: number;
    journeys_reused: number;
    links_reconciled: number;
    errors: number;
  };
  failures?: Array<{ shipment_id: number; message: string }>;
}

export type CommercialPlanShipmentRow = Pick<
  ShipmentBox,
  'exporter_receiver_id' | 'receiver' | 'ship_etd' | 'ship_eta' | 'quantity'
> & {
  box_weight?: number | null;
  total_kilos?: number | null;
};

export type DashboardSettlementShipmentRow = Pick<
  ShipmentBox,
  'exporter_receiver_id' | 'reference_number' | 'receiver' | 'ship_eta'
> & {
  id?: number | string;
};

export type ShipmentHeaderUpdate = Partial<
  Pick<
    Shipment,
    | 'exporter_receiver_id'
    | 'reference_number'
    | 'container_number'
    | 'receiver'
    | 'packing_name'
    | 'packing_etd'
    | 'ship_name'
    | 'ship_type'
    | 'ship_etd'
    | 'ship_eta'
    | 'ship_origin_port'
    | 'ship_origin_country'
    | 'ship_destination_port'
    | 'ship_destination_country'
    | 'business_type'
    | 'sale_order'
    | 'original_incoterm'
    | 'invoice_number'
    | 'sale_region'
    | 'sale_market'
    | 'freight_type'
    | 'booking_number'
    | 'awb_number'
  >
>;

const SOURCE_BOX_OPERATIONAL_FIELDS = [
  'container_number',
  'receiver',
  'packing_name',
  'packing_etd',
  'ship_name',
  'ship_type',
  'ship_etd',
  'ship_eta',
  'ship_origin_port',
  'ship_origin_country',
  'ship_destination_port',
  'ship_destination_country',
  'awb_number',
] as const;

const SHIPMENT_DETAILS_MODAL_SELECT = [
  'source_system',
  'source_shipment_external_id',
  'exporter_receiver_id',
  ...SOURCE_BOX_OPERATIONAL_FIELDS,
  'id',
  'shipment_id',
  'producer_name',
  'variety',
  'labeled_variety',
  'color',
  'caliber',
  'category',
  'box_format',
  'box_type',
  'box_weight',
  'quantity',
  'box_label',
  'fob_price',
].join(',');

const SHIPMENT_PARENT_SELECT = [
  'source_metadata',
  'id',
  'created_at',
  'exporter_id',
  'exporter_receiver_id',
  'season',
  'species_id',
  'division_id',
  'reference_number',
  'reference_key',
  'container_number',
  'receiver',
  'packing_name',
  'packing_etd',
  'ship_name',
  'ship_type',
  'ship_etd',
  'ship_eta',
  'ship_origin_port',
  'ship_destination_port',
  'ship_origin_country',
  'ship_destination_country',
  'business_type',
  'sale_order',
  'original_incoterm',
  'invoice_number',
  'sale_region',
  'sale_market',
  'freight_type',
  'booking_number',
  'awb_number',
].join(',');

const SHIPMENT_EXPORT_BOX_SELECT = [
  'source_system',
  'source_shipment_external_id',
  'exporter_receiver_id',
  ...SOURCE_BOX_OPERATIONAL_FIELDS,
  'id',
  'shipment_id',
  'producer_name',
  'variety',
  'labeled_variety',
  'caliber',
  'color',
  'box_format',
  'box_label',
  'box_type',
  'box_weight',
  'quantity',
  'pallet',
  'category',
  'lot',
  'reception_date',
  'process_internal_id',
  'process_date',
  'fob_price',
  'producer_national_id',
  'producer_group',
  'producer_sag_code',
  'box_description',
  'fruit_segregation',
  'cold_type',
  'booking_awb',
].join(',');

const NO_REFERENCE_KEY = '__SIN_REFERENCIA__';
const SHIPMENT_IDENTIFIER_VALIDATION_BATCH_SIZE = 500;
const SHIPMENT_WRITE_BATCH_SIZE = 500;
const SHIPMENT_FIELDS = [
  'exporter_receiver_id',
  'container_number',
  'receiver',
  'packing_name',
  'packing_etd',
  'ship_name',
  'ship_type',
  'ship_etd',
  'ship_eta',
  'ship_origin_port',
  'ship_origin_country',
  'ship_destination_port',
  'ship_destination_country',
  'business_type',
  'sale_order',
  'original_incoterm',
  'invoice_number',
  'sale_region',
  'sale_market',
  'freight_type',
  'booking_number',
  'awb_number',
] as const;

const SHIPMENT_BOX_INSERT_FIELDS = [
  'created_at',
  'shipment_id',
  // Legacy identity columns are still required by shipment_boxes policies/triggers.
  'exporter_id',
  'season',
  'species_id',
  'reference_number',
  'producer_name',
  'producer_sag_code',
  'producer_national_id',
  'producer_group',
  'variety',
  'labeled_variety',
  'color',
  'caliber',
  'category',
  'box_format',
  'box_type',
  'box_description',
  'box_label',
  'box_weight',
  'quantity',
  'pallet',
  'lot',
  'reception_date',
  'process_internal_id',
  'process_date',
  'fob_price',
  'fruit_segregation',
  'cold_type',
] as const;

type ShipmentPayload = Omit<
  Shipment,
  'id' | 'created_at' | 'reference_key' | 'source_metadata'
>;

const cleanText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const cleanNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const normalizeShipmentReferenceKey = (
  referenceNumber?: string | number | null
): string => cleanText(referenceNumber) || NO_REFERENCE_KEY;

const shipmentMapKey = (
  exporterId: number,
  season: string,
  speciesId: number,
  referenceNumber?: string | number | null
) =>
  `${exporterId}::${season}::${speciesId}::${normalizeShipmentReferenceKey(referenceNumber)}`;

const buildShipmentPayloadFromBox = (
  box: Partial<ShipmentBox>
): ShipmentPayload => ({
  exporter_id: Number(box.exporter_id),
  exporter_receiver_id: cleanNumber(box.exporter_receiver_id),
  season: String(box.season || ''),
  species_id: Number(box.species_id),
  reference_number: cleanText(box.reference_number),
  container_number: cleanText(box.container_number),
  receiver: cleanText(box.receiver),
  packing_name: cleanText(box.packing_name),
  packing_etd: cleanText(box.packing_etd),
  ship_name: cleanText(box.ship_name),
  ship_type: cleanText(box.ship_type),
  ship_etd: cleanText(box.ship_etd),
  ship_eta: cleanText(box.ship_eta),
  ship_origin_port: cleanText(box.ship_origin_port),
  ship_origin_country: cleanText(box.ship_origin_country),
  ship_destination_port: cleanText(box.ship_destination_port),
  ship_destination_country: cleanText(box.ship_destination_country),
  business_type: cleanText(box.business_type),
  sale_order: cleanText(box.sale_order),
  original_incoterm: cleanText(box.original_incoterm),
  invoice_number: cleanText(box.invoice_number),
  sale_region: cleanText(box.sale_region),
  sale_market: cleanText(box.sale_market),
  freight_type: cleanText(box.freight_type),
  booking_number: cleanText(box.booking_number) || cleanText(box.booking_awb),
  awb_number: cleanText(box.awb_number),
});

const mergeShipmentPayload = (
  current: ShipmentPayload,
  incoming: ShipmentPayload
) => {
  SHIPMENT_FIELDS.forEach((field) => {
    if (!current[field] && incoming[field]) {
      current[field] = incoming[field] as never;
    }
  });
  return current;
};

const serializeShipmentPayload = (payload: ShipmentPayload) => {
  const serialized = { ...payload };
  if (serialized.exporter_receiver_id == null) {
    delete serialized.exporter_receiver_id;
  }
  return serialized;
};

export const toShipmentBoxInsertPayload = (
  row: Partial<ShipmentBox> & { created_at?: string | null }
) => {
  const source = row as Record<string, unknown>;
  return SHIPMENT_BOX_INSERT_FIELDS.reduce<Record<string, unknown>>(
    (payload, field) => {
      if (source[field] !== undefined) {
        payload[field] = source[field];
      }
      return payload;
    },
    {}
  ) as Partial<ShipmentBox>;
};

export async function insertShipmentBoxesInBatches(
  rows: Partial<ShipmentBox>[]
): Promise<void> {
  for (let index = 0; index < rows.length; index += SHIPMENT_WRITE_BATCH_SIZE) {
    const { error } = await supabase
      .from('shipment_boxes')
      .insert(rows.slice(index, index + SHIPMENT_WRITE_BATCH_SIZE));

    if (error) throw error;
  }
}

export interface ShipmentImportResult {
  created: number;
  updated: number;
  skipped: number;
}

/** One RPC/transaction for the whole file; never delete/recreate a header. */
export async function importLocalShipments({
  exporterId,
  season,
  speciesId,
  rows,
  mode,
  divisionId,
}: {
  exporterId: number;
  season: string;
  speciesId: number;
  rows: Partial<ShipmentBox>[];
  mode: 'skip' | 'replace';
  /** Omitted for single-division exporters: the database uses the default. */
  divisionId?: number | null;
}): Promise<ShipmentImportResult> {
  if (!exporterId || !season?.trim() || !speciesId || rows.length === 0) {
    throw new Error(
      'La carga requiere exportadora, temporada, especie y filas.'
    );
  }
  for (const row of rows) {
    if (!isValidShipmentReference(row.reference_number)) {
      throw new Error(
        'Cada despacho requiere una referencia válida; no se permite SIN REFERENCIA.'
      );
    }
    if (
      Number(row.exporter_id) !== exporterId ||
      row.season !== season ||
      Number(row.species_id) !== speciesId
    ) {
      throw new Error(
        'Todas las filas deben pertenecer a la misma exportadora, temporada y especie.'
      );
    }
  }
  const conflicts = findShipmentUploadIdentifierConflicts(rows);
  if (conflicts.length > 0) {
    throw new Error(
      `Referencias con contenedores o AWB incompatibles: ${conflicts.map((c) => c.reference_number).join(', ')}`
    );
  }
  const { data, error } = await supabase.rpc('import_local_shipments', {
    p_exporter_id: exporterId,
    p_season: season,
    p_species_id: speciesId,
    p_mode: mode,
    p_division_id: divisionId ?? null,
    p_rows: rows.map((row) => ({
      ...toShipmentBoxInsertPayload(row),
      ...buildShipmentPayloadFromBox(row),
    })),
  });
  // PostgREST failures can be plain objects; both upload forms need an Error
  // so the database's review instruction is shown instead of a generic toast.
  if (error) throw new Error(error.message);
  const result = data as ShipmentImportResult | null;
  if (
    !result ||
    ![result.created, result.updated, result.skipped].every(
      (count) => Number.isInteger(count) && count >= 0
    )
  )
    throw new Error(
      'La carga devolvió un resultado inválido. Actualiza los despachos antes de reintentar.'
    );
  return result;
}

export async function getShipmentUploadIdentifierConflicts(
  exporterId: number,
  season: string,
  speciesId: number,
  rows: ShipmentUploadIdentifierRow[],
  divisionId?: number | null
): Promise<ShipmentUploadIdentifierConflict[]> {
  const uploadConflicts = findShipmentUploadIdentifierConflicts(rows);
  if (uploadConflicts.length > 0) return uploadConflicts;

  const referenceKeys = [
    ...new Set(
      rows
        .map((row) => normalizeShipmentReference(row.reference_number))
        .filter(Boolean)
    ),
  ];
  if (referenceKeys.length === 0) return [];

  const existingRows: ShipmentUploadIdentifierRow[] = [];
  for (
    let index = 0;
    index < referenceKeys.length;
    index += SHIPMENT_IDENTIFIER_VALIDATION_BATCH_SIZE
  ) {
    const batch = referenceKeys.slice(
      index,
      index + SHIPMENT_IDENTIFIER_VALIDATION_BATCH_SIZE
    );
    let query = supabase
      .from('shipments')
      .select('reference_number, container_number, awb_number')
      .is('source_deleted_at', null)
      .eq('exporter_id', exporterId)
      .eq('season', season)
      .eq('species_id', speciesId)
      .in('reference_key', batch);
    if (divisionId) query = query.eq('division_id', divisionId);
    const { data, error } = await query;

    if (error) throw error;
    existingRows.push(...((data || []) as ShipmentUploadIdentifierRow[]));
  }

  const incomingRowsByReference = new Map<
    string,
    ShipmentUploadIdentifierRow[]
  >();
  rows.forEach((row) => {
    const reference = normalizeShipmentReference(row.reference_number);
    if (!reference) return;
    const referenceRows = incomingRowsByReference.get(reference) || [];
    referenceRows.push(row);
    incomingRowsByReference.set(reference, referenceRows);
  });

  return existingRows
    .flatMap((existingRow) => {
      const reference = normalizeShipmentReference(
        existingRow.reference_number
      );
      const incomingRows = incomingRowsByReference.get(reference) || [];
      if (incomingRows.length === 0) return [];

      return findShipmentUploadIdentifierConflicts([
        { ...existingRow, reference_number: reference, row_number: null },
        ...incomingRows,
      ]);
    })
    .sort((left, right) =>
      left.reference_number.localeCompare(right.reference_number)
    );
}

export const flattenShipmentBox = (
  box: Partial<ShipmentBox>,
  shipment?: Partial<Shipment> | null
): ShipmentBox => {
  const sourceFields =
    box.source_system === 'frutic'
      ? Object.fromEntries(
          SOURCE_BOX_OPERATIONAL_FIELDS.map((key) => [key, box[key]])
        )
      : {};
  return {
    ...box,
    shipment_id: shipment?.id ?? box.shipment_id,
    exporter_id: shipment?.exporter_id ?? box.exporter_id,
    exporter_receiver_id:
      box.source_system === 'frutic'
        ? (box.exporter_receiver_id ?? null)
        : (shipment?.exporter_receiver_id ?? box.exporter_receiver_id ?? null),
    season: shipment?.season ?? box.season,
    species_id: shipment?.species_id ?? box.species_id,
    division_id: shipment?.division_id ?? box.division_id ?? null,
    reference_number: shipment?.reference_number ?? box.reference_number,
    container_number: shipment?.container_number ?? box.container_number,
    receiver: shipment?.receiver ?? box.receiver,
    packing_name: shipment?.packing_name ?? box.packing_name,
    packing_etd: shipment?.packing_etd ?? box.packing_etd,
    ship_name: shipment?.ship_name ?? box.ship_name,
    ship_type: (shipment?.ship_type ??
      box.ship_type) as ShipmentBox['ship_type'],
    ship_etd: shipment?.ship_etd ?? box.ship_etd,
    ship_eta: shipment?.ship_eta ?? box.ship_eta,
    ship_origin_port: shipment?.ship_origin_port ?? box.ship_origin_port,
    ship_origin_country:
      shipment?.ship_origin_country ?? box.ship_origin_country,
    ship_destination_port:
      shipment?.ship_destination_port ?? box.ship_destination_port,
    ship_destination_country:
      shipment?.ship_destination_country ?? box.ship_destination_country,
    business_type: shipment?.business_type ?? box.business_type,
    sale_order: shipment?.sale_order ?? box.sale_order,
    original_incoterm: shipment?.original_incoterm ?? box.original_incoterm,
    invoice_number: shipment?.invoice_number ?? box.invoice_number,
    sale_region: shipment?.sale_region ?? box.sale_region,
    sale_market: shipment?.sale_market ?? box.sale_market,
    freight_type: shipment?.freight_type ?? box.freight_type,
    booking_number: shipment?.booking_number ?? box.booking_number,
    awb_number: shipment?.awb_number ?? box.awb_number,
    booking_awb:
      box.booking_awb ??
      shipment?.booking_number ??
      shipment?.awb_number ??
      null,
    // Source contributions keep their own parties and logistics. Commercial
    // fields and the stable shipment identity still come from our parent.
    ...sourceFields,
  } as ShipmentBox;
};

export async function upsertShipmentsForShipmentBoxes<
  T extends Partial<ShipmentBox>,
>(boxes: T[]): Promise<T[]> {
  if (boxes.length === 0) return boxes;

  const identifierConflicts = findShipmentUploadIdentifierConflicts(boxes);
  if (identifierConflicts.length > 0) {
    const references = identifierConflicts
      .map((conflict) => conflict.reference_number)
      .join(', ');
    throw new Error(
      `Each shipment reference must identify one container or AWB. Conflicting references: ${references}`
    );
  }

  const shipmentsByKey = new Map<string, ShipmentPayload>();

  boxes.forEach((box) => {
    if (!isValidShipmentReference(box.reference_number)) {
      throw new Error('Cada despacho requiere una referencia válida.');
    }
    const payload = buildShipmentPayloadFromBox(box);
    if (!payload.exporter_id || !payload.season || !payload.species_id) {
      throw new Error(
        'Shipment rows require exporter_id, season and species_id'
      );
    }

    const key = shipmentMapKey(
      payload.exporter_id,
      payload.season,
      payload.species_id,
      payload.reference_number
    );

    const current = shipmentsByKey.get(key);
    shipmentsByKey.set(
      key,
      current ? mergeShipmentPayload(current, payload) : payload
    );
  });

  const idsByKey = new Map<string, number>();
  const shipmentPayloads = Array.from(shipmentsByKey.values()).map(
    serializeShipmentPayload
  );

  for (
    let index = 0;
    index < shipmentPayloads.length;
    index += SHIPMENT_WRITE_BATCH_SIZE
  ) {
    const { data, error } = await supabase
      .from('shipments')
      .upsert(
        shipmentPayloads.slice(index, index + SHIPMENT_WRITE_BATCH_SIZE),
        {
          onConflict: 'exporter_id,season,species_id,division_id,reference_key',
        }
      )
      .select(SHIPMENT_PARENT_SELECT);

    if (error) throw error;

    ((data || []) as unknown as Shipment[]).forEach((shipment) => {
      idsByKey.set(
        shipmentMapKey(
          shipment.exporter_id,
          shipment.season,
          shipment.species_id,
          shipment.reference_number
        ),
        shipment.id
      );
    });
  }

  return boxes.map((box) => {
    const key = shipmentMapKey(
      Number(box.exporter_id),
      String(box.season || ''),
      Number(box.species_id),
      box.reference_number
    );
    const shipmentId = idsByKey.get(key);
    if (!shipmentId) {
      throw new Error(`Could not resolve shipment_id for ${key}`);
    }
    return { ...box, shipment_id: shipmentId };
  });
}

export const deleteShipment = async (shipmentId: number) => {
  const { error } = await supabase
    .from('shipments')
    .delete()
    .eq('id', shipmentId);

  if (error) throw error;
};

export const deleteShipmentsByPacking = async (
  exporterId: number,
  season: string,
  speciesId: number,
  packingName: string
) => {
  const { error } = await supabase
    .from('shipments')
    .delete()
    .eq('exporter_id', exporterId)
    .eq('season', season)
    .eq('species_id', speciesId)
    .eq('packing_name', packingName);

  if (error) throw error;
};

// This API is only used by the shipment overview screens. Other consumers of
// the raw details RPC retain their existing contract.
export async function getShipmentsDetailsPage(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<ShipmentsDetailsPagePayload | null> {
  try {
    const { data, error } = await supabase.rpc('get_shipments_overview', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
    });
    // Frontend and database deployments can finish at different times. Only a
    // missing new RPC may fall back; permission and query failures stay visible.
    if (error?.code === 'PGRST202') {
      const legacy = await supabase.rpc('get_shipments_details_page', {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      });
      if (legacy.error) throw legacy.error;
      const payload = legacy.data as unknown as ShipmentsDetailsPagePayload;
      const tracking = new Map(
        (payload.tracking_by_shipment || []).map((entry) => [
          Number(entry.shipment_id),
          entry.tracking_journeys,
        ])
      );
      return {
        ...payload,
        rows: (payload.rows || []).map((row) => ({
          ...row,
          tracking_journeys: tracking.get(Number(row.shipment_id)) || [],
        })),
      };
    }
    if (error) throw error;
    return decodeShipmentOverview(data as unknown as ShipmentOverviewPayload);
  } catch (error) {
    console.error('Error getting shipments overview:', error);
    return null;
  }
}

export async function getShipmentBoxTypes(
  exporterId: number,
  season: string,
  speciesId: number,
  divisionId: number | null = currentDivisionId()
): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_shipment_box_types', {
    p_exporter_id: exporterId,
    p_season: season,
    p_species_id: speciesId,
    p_division_id: divisionId,
  });
  if (error) throw error;
  return (data || []) as string[];
}

async function hydrateTrackingJourneys<
  T extends { tracking_journeys?: ShipmentTrackingSummary[] },
>(rows: T[]): Promise<T[]> {
  const trackingJourneyIds = Array.from(
    new Set(
      rows.flatMap((row) =>
        (row.tracking_journeys || []).map((journey) => journey.id)
      )
    )
  );
  const eventSummaries =
    await getShipmentTrackingEventSummaries(trackingJourneyIds);
  const summaryByJourneyId = new Map(
    (eventSummaries || []).map((summary) => [
      summary.tracking_journey_id,
      summary,
    ])
  );

  return rows.map((row) => ({
    ...row,
    tracking_journeys: (row.tracking_journeys || []).map((journey) => {
      const summary = summaryByJourneyId.get(journey.id);
      return summary
        ? {
            ...journey,
            provider_container_status: summary.provider_container_status,
            observation_status: summary.observation_status,
            observation_closes_on: summary.observation_closes_on,
            cargo_arrived_at: summary.cargo_arrived_at,
            gate_out_at: summary.gate_out_at,
            empty_return_at: summary.empty_return_at,
            last_sync_succeeded_at: summary.last_sync_succeeded_at,
            consecutive_sync_failures: summary.consecutive_sync_failures,
            latest_actual_event: summary.latest_actual_event || null,
            latest_estimated_event: summary.latest_estimated_event || null,
          }
        : journey;
    }),
  }));
}

export async function getShipmentTrackingSummaries(
  exporterId: number,
  season: string,
  speciesId: number,
  referenceNumbers: Array<string | number | null | undefined>,
  divisionId: number | null = currentDivisionId()
): Promise<ShipmentTrackingReferenceSummary[] | null> {
  const referenceKeys = Array.from(
    new Set(referenceNumbers.map(normalizeShipmentReferenceKey))
  );
  if (referenceKeys.length === 0) return [];

  try {
    const { data, error } = await supabase.rpc(
      'get_shipment_tracking_summaries',
      {
        p_exporter_id: exporterId,
        p_season: season,
        p_species_id: speciesId,
        p_reference_keys: referenceKeys,
        p_division_id: divisionId,
      }
    );
    if (error) throw error;
    return hydrateTrackingJourneys(
      (data || []) as unknown as ShipmentTrackingReferenceSummary[]
    );
  } catch (error) {
    console.error('Error getting shipment tracking summaries:', error);
    return null;
  }
}

export async function getShipmentTrackingEventSummaries(
  trackingJourneyIds: string[]
): Promise<ShipmentTrackingEventSummaryRow[] | null> {
  if (trackingJourneyIds.length === 0) return [];

  try {
    const { data, error } = await supabase.rpc(
      'get_shipment_tracking_event_summaries',
      { p_tracking_journey_ids: trackingJourneyIds }
    );
    if (error) throw error;
    return (data || []) as unknown as ShipmentTrackingEventSummaryRow[];
  } catch (error) {
    console.error('Error getting shipment tracking event summaries:', error);
    return null;
  }
}

export async function refreshShipmentTrackingJourney(
  trackingJourneyId: string
): Promise<ShipmentTrackingRefreshResult> {
  const { data, error } = await supabase.functions.invoke(
    'sync-active-shipment-tracking',
    {
      body: { journey_ids: [trackingJourneyId], limit: 1 },
    }
  );
  const result = data as ShipmentTrackingRefreshResult | null;
  if (error) throw new Error(result?.error || error.message);
  if (!result?.success) {
    const firstFailure = result?.results?.find(
      (entry) => entry.result !== 'succeeded'
    );
    throw new Error(
      firstFailure?.error ||
        firstFailure?.error_code ||
        result?.error ||
        'Could not refresh shipment tracking'
    );
  }
  return result;
}

export async function discoverShipmentTrackingAfterEdit(
  shipmentId: number
): Promise<ShipmentTrackingDiscoveryResult> {
  const { data, error } = await supabase.functions.invoke(
    'discover-shipment-tracking',
    {
      body: {
        shipment_ids: [shipmentId],
        limit: 1,
        sync_frutic: false,
      },
    }
  );
  const result = data as ShipmentTrackingDiscoveryResult | null;
  if (error) throw new Error(result?.error || error.message);
  if (!result?.success) {
    throw new Error(
      result?.failures?.[0]?.message ||
        result?.error ||
        'Could not reconcile shipment tracking'
    );
  }
  return result;
}

export async function getShipmentTrackingEvents(
  trackingJourneyIds: string[]
): Promise<ShipmentTrackingEvent[] | null> {
  if (trackingJourneyIds.length === 0) return [];

  try {
    const { data, error } = await supabase
      .from('shipment_tracking_events')
      .select(
        [
          'id',
          'tracking_journey_id',
          'event_type',
          'event_key',
          'revision_fingerprint',
          'provider_status',
          'description',
          'location_name',
          'location_locode',
          'location_country_name',
          'location_country_code',
          'vessel_name',
          'vessel_imo',
          'voyage_number',
          'occurred_at',
          'location_timezone:raw_payload->location->>timezone',
          'is_estimated',
          'latitude',
          'longitude',
        ].join(',')
      )
      .in('tracking_journey_id', trackingJourneyIds)
      .order('occurred_at', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as ShipmentTrackingEvent[];
  } catch (error) {
    console.error('Error getting shipment tracking events:', error);
    return null;
  }
}

export async function getShipmentTrackingEventRevisions(
  trackingJourneyIds: string[]
): Promise<ShipmentTrackingEventRevision[] | null> {
  if (trackingJourneyIds.length === 0) return [];

  try {
    const { data, error } = await supabase
      .from('shipment_tracking_event_revisions')
      .select(
        [
          'id',
          'tracking_journey_id',
          'first_snapshot_id',
          'last_snapshot_id',
          'event_key',
          'event_fingerprint',
          'revision_fingerprint',
          'event_type',
          'provider_status',
          'description',
          'location_name',
          'location_locode',
          'location_country_name',
          'location_country_code',
          'vessel_name',
          'vessel_imo',
          'voyage_number',
          'occurred_at',
          'location_timezone:raw_payload->location->>timezone',
          'is_estimated',
          'latitude',
          'longitude',
          'first_seen_at',
          'last_seen_at',
          'seen_count',
        ].join(',')
      )
      .in('tracking_journey_id', trackingJourneyIds)
      .order('last_seen_at', { ascending: true })
      .order('first_seen_at', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as ShipmentTrackingEventRevision[];
  } catch (error) {
    console.error('Error getting shipment tracking event revisions:', error);
    return null;
  }
}

export async function getShipmentTrackingHistoryByShipmentId(
  shipmentId: number
): Promise<ShipmentTrackingEventRevision[] | null> {
  try {
    const { data, error } = await supabase
      .from('shipment_tracking_event_history')
      .select(
        [
          'id:event_revision_id',
          'tracking_journey_id',
          'event_key',
          'event_fingerprint',
          'revision_fingerprint',
          'event_type',
          'provider_status',
          'description',
          'location_name',
          'location_locode',
          'location_country_name',
          'location_country_code',
          'vessel_name',
          'vessel_imo',
          'voyage_number',
          'occurred_at',
          'is_estimated',
          'latitude',
          'longitude',
          'first_seen_at',
          'last_seen_at',
          'seen_count',
        ].join(',')
      )
      .eq('shipment_id', shipmentId)
      .order('last_seen_at', { ascending: true })
      .order('first_seen_at', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as ShipmentTrackingEventRevision[];
  } catch (error) {
    console.error('Error getting shipment tracking history:', error);
    return null;
  }
}

export async function getShipmentShipTripsByShipmentId(
  shipmentId: number
): Promise<ShipmentShipTrip[] | null> {
  try {
    const { data, error } = await supabase
      .from('shipment_ship_trips')
      .select('*')
      .eq('shipment_id', shipmentId)
      .order('leg_sequence', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as ShipmentShipTrip[];
  } catch (error) {
    console.error('Error getting shipment ship trips:', error);
    return null;
  }
}

/** Scope the canonical identity read even when a caller already knows the ID. */
export async function getShipmentForId(
  exporterId: number,
  season: string,
  speciesId: number,
  shipmentId: number
): Promise<Shipment | null> {
  const { data, error } = await supabase
    .from('shipments')
    .select(SHIPMENT_PARENT_SELECT)
    .is('source_deleted_at', null)
    .eq('exporter_id', exporterId)
    .eq('season', season)
    .eq('species_id', speciesId)
    .eq('id', shipmentId)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as Shipment | null;
}

/** Full active packing, paginated by box ID, using the saved parent scope. */
export async function getShipmentBoxesForShipment(
  shipment: Shipment
): Promise<ShipmentBox[]> {
  const boxes: ShipmentBox[] = [];
  let afterId = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('shipment_boxes')
      .select(SHIPMENT_DETAILS_MODAL_SELECT)
      .is('source_deleted_at', null)
      .eq('shipment_id', shipment.id)
      .eq('exporter_id', shipment.exporter_id)
      .eq('season', shipment.season)
      .eq('species_id', shipment.species_id)
      .gt('id', afterId)
      .order('id', { ascending: true })
      .limit(1000);
    if (error) throw error;
    const page = (data || []) as unknown as ShipmentBox[];
    boxes.push(...page.map((box) => flattenShipmentBox(box, shipment)));
    if (page.length < 1000) return boxes;
    const next = Number(page[page.length - 1].id);
    if (!Number.isFinite(next) || next <= afterId)
      throw new Error('Invalid shipment packing cursor');
    afterId = next;
  }
}

export const AMBIGUOUS_SHIPMENT_REFERENCE_MESSAGE =
  'La referencia existe en más de una división; selecciona la división.';

/** Null on request failure; throws when the reference is shared by divisions. */
export async function getShipmentBoxesForReference(
  exporter_id: number,
  season: string,
  species_id: number,
  reference_number: string,
  divisionId?: number | null
): Promise<ShipmentBox[] | null> {
  let shipments: Shipment[];
  try {
    let shipmentQuery = supabase
      .from('shipments')
      .select(SHIPMENT_PARENT_SELECT)
      .is('source_deleted_at', null)
      .eq('exporter_id', exporter_id)
      .eq('season', season)
      .eq('species_id', species_id);
    if (divisionId) shipmentQuery = shipmentQuery.eq('division_id', divisionId);

    if (reference_number === 'SIN REFERENCIA') {
      shipmentQuery = shipmentQuery.eq('reference_key', NO_REFERENCE_KEY);
    } else {
      shipmentQuery = shipmentQuery.eq(
        'reference_key',
        normalizeShipmentReferenceKey(reference_number)
      );
    }

    // Two rows are enough to tell a unique reference from a shared one.
    const { data, error: shipmentError } = await shipmentQuery.limit(2);
    if (shipmentError) throw shipmentError;
    shipments = (data || []) as unknown as Shipment[];
  } catch (error) {
    console.error('Error getting shipment boxes for reference:', error);
    return null;
  }
  if (shipments.length === 0) return [];
  if (shipments.length > 1) {
    throw new Error(AMBIGUOUS_SHIPMENT_REFERENCE_MESSAGE);
  }
  try {
    return await getShipmentBoxesForShipment(shipments[0]);
  } catch (error) {
    console.error('Error getting shipment boxes for reference:', error);
    return null;
  }
}

export const SHIPMENT_DIVISION_CONFLICT_MESSAGE =
  'Ya existe un despacho con esta referencia en esa división.';
export const SHIPMENT_DIVISION_PERMISSION_MESSAGE =
  'No tienes permiso para mover las liquidaciones de este despacho.';
export const SHIPMENT_DIVISION_OWNER_MESSAGE =
  'La división no pertenece a la exportadora.';

/** Moves a shipment to another division of its exporter; its settlements follow (DB trigger). */
export async function updateShipmentDivision(
  shipmentId: number,
  divisionId: number
): Promise<Shipment> {
  const { data, error } = await supabase
    .from('shipments')
    .update({ division_id: divisionId })
    .eq('id', shipmentId)
    .select('*')
    .single();
  if (error) {
    if (error.code === '23505')
      throw new Error(SHIPMENT_DIVISION_CONFLICT_MESSAGE);
    if (error.code === '42501')
      throw new Error(SHIPMENT_DIVISION_PERMISSION_MESSAGE);
    if (error.code === '23503')
      throw new Error(SHIPMENT_DIVISION_OWNER_MESSAGE);
    throw new Error(error.message);
  }
  return data as unknown as Shipment;
}

export async function getShipmentForReference(
  exporter_id: number,
  season: string,
  species_id: number,
  reference_number: string
): Promise<Shipment | null> {
  try {
    let shipmentQuery = supabase
      .from('shipments')
      .select(SHIPMENT_PARENT_SELECT)
      .is('source_deleted_at', null)
      .eq('exporter_id', exporter_id)
      .eq('season', season)
      .eq('species_id', species_id);

    if (reference_number === 'SIN REFERENCIA') {
      shipmentQuery = shipmentQuery.eq('reference_key', NO_REFERENCE_KEY);
    } else {
      shipmentQuery = shipmentQuery.eq(
        'reference_key',
        normalizeShipmentReferenceKey(reference_number)
      );
    }

    const { data, error } = await shipmentQuery.maybeSingle();
    if (error) throw error;

    return (data || null) as unknown as Shipment | null;
  } catch (error) {
    console.error('Error getting shipment for reference:', error);
    return null;
  }
}

export async function updateShipmentHeader(
  shipment_id: number,
  updates: ShipmentHeaderUpdate
): Promise<Shipment> {
  if (
    'reference_number' in updates &&
    !isValidShipmentReference(updates.reference_number)
  ) {
    throw new Error('Cada despacho requiere una referencia válida.');
  }
  const sanitized = Object.fromEntries(
    Object.entries(updates).map(([key, value]) => [
      key,
      typeof value === 'string' && value.trim() === '' ? null : value,
    ])
  ) as ShipmentHeaderUpdate;

  const { data, error } = await supabase
    .from('shipments')
    .update(sanitized)
    .eq('id', shipment_id)
    .select(SHIPMENT_PARENT_SELECT)
    .single();

  if (error) throw error;
  return data as unknown as Shipment;
}

export async function getShipmentBoxesForContainer(
  exporter_id: number,
  season: string,
  species_id: number,
  container_number: string
): Promise<ShipmentBox[] | null> {
  try {
    const normalizeContainer = (value: string | null | undefined) =>
      (value || '').replace(/[-/\s]+/g, '').toUpperCase();
    const target = normalizeContainer(container_number);

    const { data: shipments, error: shipmentsError } = await supabase
      .from('shipments')
      .select(SHIPMENT_PARENT_SELECT)
      .is('source_deleted_at', null)
      .eq('exporter_id', exporter_id)
      .eq('season', season)
      .eq('species_id', species_id);

    if (shipmentsError) throw shipmentsError;

    const matchingShipments = (
      (shipments || []) as unknown as Shipment[]
    ).filter(
      (shipment) =>
        normalizeContainer(shipment.container_number) === target ||
        (Array.isArray(shipment.source_metadata?.tracking_references) &&
          shipment.source_metadata.tracking_references.some(
            (value: unknown) =>
              typeof value === 'string' && normalizeContainer(value) === target
          ))
    );

    if (matchingShipments.length === 0) return [];

    const shipmentsById = new Map(
      matchingShipments.map((shipment) => [shipment.id, shipment])
    );

    const { data: boxes, error: boxesError } = await supabase
      .from('shipment_boxes')
      .select(SHIPMENT_EXPORT_BOX_SELECT)
      .is('source_deleted_at', null)
      .in(
        'shipment_id',
        matchingShipments.map((shipment) => shipment.id)
      );

    if (boxesError) throw boxesError;

    return ((boxes || []) as unknown as ShipmentBox[])
      .filter(
        (box) =>
          box.source_system !== 'frutic' ||
          normalizeContainer(box.container_number) === target
      )
      .map((box) =>
        flattenShipmentBox(box, shipmentsById.get(Number(box.shipment_id)))
      );
  } catch (error) {
    console.error('Error getting shipment boxes for container:', error);
    return null;
  }
}

export async function getShipmentBoxesForExport(
  exporter_id: number,
  season: string,
  species_id: number
): Promise<ShipmentBox[] | null> {
  try {
    const allData: ShipmentBox[] = [];
    let shipmentFrom = 0;
    const shipmentBatchSize = 500;
    const boxBatchSize = 1000;

    while (true) {
      const { data: shipments, error: shipmentError } = await supabase
        .from('shipments')
        .select(SHIPMENT_PARENT_SELECT)
        .is('source_deleted_at', null)
        .eq('exporter_id', exporter_id)
        .eq('season', season)
        .eq('species_id', species_id)
        .order('id', { ascending: true })
        .range(shipmentFrom, shipmentFrom + shipmentBatchSize - 1);

      if (shipmentError) throw shipmentError;
      if (!shipments || shipments.length === 0) break;

      const parentRows = shipments as unknown as Shipment[];
      const shipmentsById = new Map(
        parentRows.map((shipment) => [shipment.id, shipment])
      );
      const shipmentIds = parentRows.map((shipment) => shipment.id);
      let boxFrom = 0;

      while (true) {
        const { data: boxes, error: boxesError } = await supabase
          .from('shipment_boxes')
          .select(SHIPMENT_EXPORT_BOX_SELECT)
          .is('source_deleted_at', null)
          .in('shipment_id', shipmentIds)
          .order('id', { ascending: true })
          .range(boxFrom, boxFrom + boxBatchSize - 1);

        if (boxesError) throw boxesError;
        if (!boxes || boxes.length === 0) break;

        allData.push(
          ...((boxes || []) as unknown as ShipmentBox[]).map((box) =>
            flattenShipmentBox(box, shipmentsById.get(Number(box.shipment_id)))
          )
        );

        if (boxes.length < boxBatchSize) break;
        boxFrom += boxBatchSize;
      }

      if (shipments.length < shipmentBatchSize) break;
      shipmentFrom += shipmentBatchSize;
    }

    return allData;
  } catch (error) {
    console.error('Error getting shipment boxes for export:', error);
    return null;
  }
}

// Use bounded reads for full-season consumers. Building one large JSON RPC
// response can exceed the authenticated statement timeout on real seasons.
export async function getPreSettlementShipmentBoxes(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<ShipmentBox[] | null> {
  const rows: ShipmentBox[] = [];
  const pageSize = 1000;
  let afterId = 0;
  while (true) {
    const { data, error } = await supabase.rpc('get_shipment_boxes_page', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_after_id: afterId,
      p_limit: pageSize,
    });
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error('Invalid shipment boxing page');
    const page = data as unknown as ShipmentBox[];
    let cursor = afterId;
    for (const box of page) {
      const id = Number(box.id);
      if (!Number.isSafeInteger(id) || id <= cursor)
        throw new Error('Invalid shipment boxing cursor');
      cursor = id;
    }
    rows.push(...page);
    if (page.length < pageSize) return rows;
    afterId = cursor;
  }
}

export async function getCommercialPlanShipments(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<CommercialPlanShipmentRow[] | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_commercial_plan_shipments',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;
    return (data || []) as unknown as CommercialPlanShipmentRow[];
  } catch (error) {
    console.error('Error getting commercial plan shipments:', error);
    return null;
  }
}

export async function getDashboardSettlementShipments(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<DashboardSettlementShipmentRow[] | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_dashboard_settlement_shipments',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;
    return (data || []) as unknown as DashboardSettlementShipmentRow[];
  } catch (error) {
    console.error('Error getting dashboard settlement shipments:', error);
    return null;
  }
}

export type ShipmentHeaderRow = {
  id: number;
  reference_number: string;
  receiver: string | null;
  exporter_receiver_id: number | null;
  ship_name: string | null;
  ship_etd: string | null;
  ship_eta: string | null;
};

export type ShipmentReplacement = { id: string; reference: string };

export async function getShipmentHeaders(
  exporterId: number,
  season: string,
  speciesId: number,
  divisionId: number | null = currentDivisionId()
): Promise<ShipmentHeaderRow[]> {
  const { data, error } = await supabase.rpc('get_shipment_headers', {
    p_exporter_id: exporterId,
    p_season: season,
    p_species_id: speciesId,
    p_division_id: divisionId,
  });
  if (error) throw error;
  return (data || []) as unknown as ShipmentHeaderRow[];
}

export async function getShipmentReconciliationCases(
  exporterId: number,
  season: string,
  speciesId: number
): Promise<ShipmentReplacement[]> {
  const { data, error } = await supabase.rpc(
    'get_shipment_reconciliation_cases',
    {
      p_exporter_id: exporterId,
      p_season: season,
      p_species_id: speciesId,
    }
  );
  if (error) throw error;
  return (data || []) as unknown as ShipmentReplacement[];
}
